import React from 'react';
import { Phone, CheckCircle, Clock, Shield, ArrowRight, Droplets } from 'lucide-react';
import { Button } from './ui/button';

const ICON_MAP = { CheckCircle, Clock, Shield, Phone, Droplets };

const DEFAULTS = {
  badge: "Jaipur's Most Trusted RO Service Provider",
  title: 'Pure Water for a',
  highlight: 'Healthier Tomorrow',
  subtitle: 'Expert RO installation, repair & maintenance in Jaipur. Certified technicians. Genuine parts. Same-day service.',
  cta_primary: 'Book Service Now',
  cta_secondary: 'Call: 9887068888',
  phone: '+919887068888',
  stats: [
    { value: '5000+', label: 'Happy Families' },
    { value: '10+', label: 'Years Experience' },
    { value: '24/7', label: 'Support' },
  ],
  trust_badges: [
    { icon: 'CheckCircle', text: 'Certified Technicians' },
    { icon: 'Clock', text: 'Same-Day Service' },
    { icon: 'Shield', text: 'Service Warranty' },
  ],
};

const Hero = ({ cms }) => {
  const d = cms || {};
  const badge = d.badge_text || d.tagline || DEFAULTS.badge;
  const title = d.title || DEFAULTS.title;
  const highlight = d.highlight_text || DEFAULTS.highlight;
  const subtitle = d.content || d.subtitle || DEFAULTS.subtitle;
  const ctaPrimary = d.cta_primary || DEFAULTS.cta_primary;
  const phone = d.phone || DEFAULTS.phone;
  const stats = d.stats || DEFAULTS.stats;
  const trustBadges = d.trust_badges || DEFAULTS.trust_badges;

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-[92vh] flex items-center bg-[#0a1628] overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f2847] to-[#0a2040]" />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '48px 48px'
      }} />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 18 }, (_, i) => (
          <div key={i} className="water-bubble absolute rounded-full" style={{
            width: `${Math.random() * 5 + 3}px`, height: `${Math.random() * 5 + 3}px`,
            left: `${Math.random() * 100}%`, bottom: '-10px',
            background: i % 3 === 0 ? `rgba(132,204,22,${Math.random()*0.35+0.1})` : `rgba(96,165,250,${Math.random()*0.25+0.1})`,
            animationDelay: `${Math.random()*12}s`, animationDuration: `${Math.random()*8+12}s`,
          }} />
        ))}
      </div>

      <div className="absolute top-16 right-[15%] w-[500px] h-[500px] bg-blue-500/[0.035] rounded-full blur-[120px]" />
      <div className="absolute bottom-16 left-[8%] w-[400px] h-[400px] bg-lime-500/[0.025] rounded-full blur-[100px]" />

      <div className="container mx-auto px-4 relative z-10 pb-28 pt-8 md:pt-0">
        <div className="grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 bg-lime-400/10 text-lime-400 px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-6 md:mb-8 border border-lime-400/20" data-testid="hero-badge">
              <Shield size={14} />
              {badge}
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-5 md:mb-6 leading-[1.08] tracking-tight">
              {title}<br />
              <span className="text-lime-400">{highlight}</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-blue-200/70 mb-8 md:mb-10 max-w-xl leading-relaxed">{subtitle}</p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 md:mb-10">
              <Button onClick={scrollToContact} data-testid="hero-book-service-btn"
                className="bg-lime-500 hover:bg-lime-400 text-gray-900 px-7 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-xl font-bold shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-all group">
                {ctaPrimary}
                <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <a href={`tel:${phone}`} data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/15 px-7 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-xl font-semibold transition-all backdrop-blur-sm">
                <Phone size={18} />
                Call: {phone.replace('+91', '').replace('+', '')}
              </a>
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs sm:text-sm text-blue-200/60">
              {trustBadges.map((b, i) => {
                const Icon = ICON_MAP[b.icon] || CheckCircle;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <Icon className="text-lime-400/70" size={15} />
                    {b.text}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hidden lg:flex lg:col-span-2 items-center justify-center">
            <div className="relative w-[320px] h-[320px]">
              <div className="absolute inset-0 rounded-full border border-white/[0.06] water-ripple-1" />
              <div className="absolute inset-8 rounded-full border border-white/[0.08] water-ripple-2" />
              <div className="absolute inset-16 rounded-full border border-lime-400/[0.1] water-ripple-3" />
              <div className="absolute inset-24 rounded-full bg-gradient-to-br from-lime-400/[0.06] to-blue-400/[0.04]" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Droplets className="text-lime-400/30" size={36} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-white/[0.04] backdrop-blur-md border-t border-white/[0.08]">
        <div className="container mx-auto px-4 py-4 sm:py-5">
          <div className="grid grid-cols-3 gap-4">
            {stats.map((s, i) => (
              <div key={i} className="text-center" data-testid={`hero-stat-${i}`}>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-lime-400">{s.value}</div>
                <div className="text-[10px] sm:text-xs md:text-sm text-blue-200/50 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
